import type { CcyCode } from './currencies.js';
import { D, MONEY_SCALE, type Money } from './decimal.js';

export interface ProfitInput {
  clGiveCcy: CcyCode;
  clGiveAmt: Money;
  clGetCcy: CcyCode;
  clGetAmt: Money;
  exGiveCcy: CcyCode;
  exGiveAmt: Money;
  exGetCcy: CcyCode;
  exGetAmt: Money;
  /**
   * Снимок курсов USDT на момент расчёта.
   * Каждый rate = «сколько единиц валюты эквивалентно 1 USDT».
   * Обязательный — без курсов прибыль не определена.
   */
  rates: Record<CcyCode, Money>;
  /**
   * Бонус исполнителю (благодарность за проведённую сделку) в USDT. В отличие от
   * фикса/коэффициента (pass-through — платит клиент/реферер, профит не меняют),
   * бонус оплачивается из маржи компании → вычитается из профита: нетто =
   * спред − бонус. Отсутствие/0 = без бонуса.
   */
  bonusUsdt?: Money;
}

export interface ProfitResult {
  /** ВСЕГДА в USDT (signed: положительный = прибыль, отрицательный = убыток). */
  amount: Money;
  ccy: 'USDT';
  /** Признак, что профит вычислен. В универсальной USDT-модели всегда true. */
  determined: true;
  /**
   * Опциональное точное значение профита в исходной валюте — заполняется только
   * для same-currency сделок (clGiveCcy === exGetCcy ИЛИ clGetCcy === exGiveCcy),
   * где результат не зависит от USDT-курса. Для UI как «∼15 000 ₽».
   */
  nativeAmount?: Money;
  nativeCcy?: CcyCode;
  /** Снимок курсов, использованных в расчёте (эхо входа). Сохраняется в БД. */
  ratesSnapshot: Record<CcyCode, Money>;
  /** USDT-эквивалент того, что мы получаем от клиента. Для UI «путь через USDT». */
  receiveUsdt: Money;
  /** USDT-эквивалент того, что мы отдаём исполнителю. Для UI «путь через USDT». */
  payUsdt: Money;
}

/**
 * Универсальная USDT-модель расчёта профита.
 *
 * Бизнес-модель: мы балансируем сделку через USDT-пул. Получаем валюту X от
 * клиента → конвертируем в USDT → закупаем у исполнителя валюту Y, которую
 * передаём клиенту. Профит = USDT-эквивалент полученного − USDT-эквивалент отданного.
 *
 * Формула:
 *   receiveUsdt = clGiveAmt / rates[clGiveCcy]      // что нам приходит от клиента
 *   payUsdt     = exGetAmt  / rates[exGetCcy]       // что мы отдаём исполнителю (обмен)
 *   profit      = receiveUsdt − payUsdt − bonusUsdt // нетто, signed
 *
 * Бонус исполнителю (если задан) вычитается из профита — это благодарность,
 * оплачиваемая из маржи компании (см. bonusUsdt). receiveUsdt/payUsdt — путь
 * обмена, бонус в них НЕ входит.
 *
 * Для same-currency сделок дополнительно вычисляется точное значение в
 * исходной валюте (поле nativeAmount): спред — без зависимости от курсов,
 * вычет бонуса — по курсу снапшота для валюты native.
 *
 * Throws при отсутствующих или нулевых курсах — лучше явный fail, чем тихий 0.
 */
export function calcProfit(input: ProfitInput): ProfitResult {
  const giveRate = input.rates[input.clGiveCcy];
  const payRate = input.rates[input.exGetCcy];

  if (!giveRate || D(giveRate).lte(0)) {
    throw new Error(`missing USDT rate for ${input.clGiveCcy}`);
  }
  if (!payRate || D(payRate).lte(0)) {
    throw new Error(`missing USDT rate for ${input.exGetCcy}`);
  }

  // Бонус исполнителю: всегда в USDT, оплачивается из маржи → вычитается из
  // профита (нетто). Отсутствие/0 = без бонуса.
  const bonusUsdt =
    input.bonusUsdt && !D(input.bonusUsdt).isZero() ? D(input.bonusUsdt) : null;

  const receiveUsdt = D(input.clGiveAmt).dividedBy(D(giveRate)).toDecimalPlaces(MONEY_SCALE);
  const payUsdt = D(input.exGetAmt).dividedBy(D(payRate)).toDecimalPlaces(MONEY_SCALE);
  const profitUsdt = receiveUsdt
    .minus(payUsdt)
    .minus(bonusUsdt ?? 0)
    .toDecimalPlaces(MONEY_SCALE);

  // Native fallback: точное значение в исходной валюте для same-ccy сделок.
  let nativeAmount: Money | undefined;
  let nativeCcy: CcyCode | undefined;
  if (input.clGiveCcy === input.exGetCcy) {
    nativeAmount = D(input.clGiveAmt).minus(D(input.exGetAmt)).toDecimalPlaces(MONEY_SCALE).toString();
    nativeCcy = input.clGiveCcy;
  } else if (input.clGetCcy === input.exGiveCcy) {
    nativeAmount = D(input.exGiveAmt).minus(D(input.clGetAmt)).toDecimalPlaces(MONEY_SCALE).toString();
    nativeCcy = input.clGetCcy;
  }

  // Native тоже нетто: спред − бонус в валюте native по курсу снапшота. Для
  // same-ccy (clGiveCcy===exGetCcy) это ровно замороженный bonusExAmt (та же
  // валюта и курс). Нет курса native → оставляем спред (редко; USDT-нетто верен).
  if (bonusUsdt && nativeAmount !== undefined && nativeCcy) {
    const nativeRate = input.rates[nativeCcy];
    if (nativeRate && D(nativeRate).gt(0)) {
      const bonusNative = bonusUsdt.times(D(nativeRate)).toDecimalPlaces(MONEY_SCALE);
      nativeAmount = D(nativeAmount).minus(bonusNative).toDecimalPlaces(MONEY_SCALE).toString();
    }
  }

  return {
    amount: profitUsdt.toString(),
    ccy: 'USDT',
    determined: true,
    nativeAmount,
    nativeCcy,
    ratesSnapshot: input.rates,
    receiveUsdt: receiveUsdt.toString(),
    payUsdt: payUsdt.toString(),
  };
}
